import React, { useEffect, useState, useCallback } from 'react';
import cemeteryApi from '../services/cemeteryApi';
import { Plus, Edit, Trash2, BarChart2, RefreshCw, Building, MapPin, Image, Settings } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import Button from '../components/Button';

const Cemeteries = () => {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const [cemeteries, setCemeteries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', description: '' });
  const [searchInput, setSearchInput] = useState('');
  const [showMapSettings, setShowMapSettings] = useState(false);
  const [mapSettings, setMapSettings] = useState({
    corners: {
      northEastLat: '', northEastLng: '',
      northWestLat: '', northWestLng: '',
      southEastLat: '', southEastLng: '',
      southWestLat: '', southWestLng: ''
    },
    tiles: {
      level0X: '', level0Y: '',
      level1X: '', level1Y: '',
      level2X: '', level2Y: ''
    },
    defaultStartingLocation: {
      defaultStartingLat: '',
      defaultStartingLng: ''
    }
  });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);

  const fetchCemeteries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await cemeteryApi.getAll();
      setCemeteries(data.data || data); // API 구조에 따라 data.data 또는 data
    } catch (e) {
      const errorMessage = e.message || '묘지 목록을 불러오는 중 오류가 발생했습니다.';
      setError(errorMessage);
      showError(errorMessage);
    }
    setLoading(false);
  }, [showError]);

  useEffect(() => { fetchCemeteries(); }, [fetchCemeteries]);

  const handleEdit = (cemetery) => {
    setEditing(cemetery);
    setForm({
      name: cemetery.name,
      address: cemetery.address,
      description: cemetery.description || ''
    });
    setShowForm(true);
  };

  const handleMapSettings = async (cemetery) => {
    setEditing(cemetery);
    try {
      // 기존 지도 정보 로드
      const mapInfo = await cemeteryApi.getMapInfo(cemetery.id);
      if (mapInfo) {
        setMapSettings({
          corners: {
            northEastLat: mapInfo.corners?.north_east_lat || '',
            northEastLng: mapInfo.corners?.north_east_lng || '',
            northWestLat: mapInfo.corners?.north_west_lat || '',
            northWestLng: mapInfo.corners?.north_west_lng || '',
            southEastLat: mapInfo.corners?.south_east_lat || '',
            southEastLng: mapInfo.corners?.south_east_lng || '',
            southWestLat: mapInfo.corners?.south_west_lat || '',
            southWestLng: mapInfo.corners?.south_west_lng || ''
          },
          tiles: {
            level0X: mapInfo.tiles?.level_0_x || '',
            level0Y: mapInfo.tiles?.level_0_y || '',
            level1X: mapInfo.tiles?.level_1_x || '',
            level1Y: mapInfo.tiles?.level_1_y || '',
            level2X: mapInfo.tiles?.level_2_x || '',
            level2Y: mapInfo.tiles?.level_2_y || ''
          },
          defaultStartingLocation: {
            defaultStartingLat: mapInfo.defaultStartingLocation?.default_starting_lat || '',
            defaultStartingLng: mapInfo.defaultStartingLocation?.default_starting_lng || ''
          }
        });
      }

      // 프로필 이미지 로드
      try {
        const profileImg = await cemeteryApi.getProfileImage(cemetery.id);
        setProfileImage(profileImg?.profile_image_url || null);
      } catch (e) {
        setProfileImage(null);
      }

      setShowMapSettings(true);
    } catch (e) {
      showError('지도 정보를 불러오는데 실패했습니다.');
    }
  };

  const handleMapSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      // 지도 꼭짓점 좌표 저장
      await cemeteryApi.saveMapCorners(editing.id, mapSettings.corners);
      
      // 지도 타일 정보 저장
      await cemeteryApi.saveMapTiles(editing.id, mapSettings.tiles);
      
      // 기본 출발 위치 저장
      await cemeteryApi.saveDefaultStartingLocation(editing.id, mapSettings.defaultStartingLocation);
      
      // 프로필 이미지 업로드
      if (profileImageFile) {
        await cemeteryApi.uploadProfileImage(editing.id, profileImageFile);
      }
      
      success('지도 설정이 성공적으로 저장되었습니다.');
      setShowMapSettings(false);
      setProfileImageFile(null);
    } catch (e) {
      showError(e.message || '지도 설정 저장에 실패했습니다.');
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setProfileImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteProfileImage = async () => {
    try {
      await cemeteryApi.deleteProfileImage(editing.id);
      setProfileImage(null);
      setProfileImageFile(null);
      success('프로필 이미지가 삭제되었습니다.');
    } catch (e) {
      showError(e.message || '프로필 이미지 삭제에 실패했습니다.');
    }
  };



  const handleToggleStatus = async (cemetery) => {
    try {
      await cemeteryApi.toggleStatus(cemetery.id);
      success(`묘지 상태가 성공적으로 변경되었습니다.`);
      fetchCemeteries();
    } catch (e) {
      showError(e.message || '묘지 상태 변경에 실패했습니다.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await cemeteryApi.update(editing.id, form);
        success('묘지가 성공적으로 수정되었습니다.');
      } else {
        await cemeteryApi.create(form);
        success('묘지가 성공적으로 생성되었습니다.');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', address: '', description: '' });
      fetchCemeteries();
    } catch (e) {
      showError(e.message || (editing ? '묘지 수정에 실패했습니다.' : '묘지 생성에 실패했습니다.'));
    }
  };

  const filteredCemeteries = cemeteries.filter(cemetery => 
    cemetery.name?.includes(searchInput) || cemetery.address?.includes(searchInput)
  );

  if (loading) {
    return <div className="loading">묘지 정보를 불러오는 중...</div>;
  }

  // 권한 확인
  if (!hasPermission(user?.role, 'CEMETERY_VIEW')) {
    return (
      <div className="flex-center min-h-screen">
        <div className="text-center">
          <Building size={64} className="mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">접근 권한이 없습니다</h2>
          <p className="text-gray-500">묘지 관리 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">묘지 관리</h1>
        <Button 
          variant="primary"
          icon={Plus}
          onClick={() => { 
            setShowForm(true); 
            setEditing(null); 
            setForm({ name: '', address: '', description: '' }); 
          }}
        >
          묘지 추가
        </Button>
      </div>

      {/* 검색 필터 */}
      <div className="card mb-6">
        <div className="grid grid-3" style={{ gap: '16px', alignItems: 'end' }}>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">
              검색
            </label>
            <input
              type="text"
              placeholder="묘지명, 주소 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="input w-full"
            />
          </div>
          <div className="text-sm text-gray-500">
            총 {filteredCemeteries.length}개 묘지
          </div>
          <div className="flex gap-2">
            <Button 
              type="button" 
              variant="secondary"
              onClick={() => setSearchInput('')}
            >
              초기화
            </Button>
            <Button 
              type="button" 
              variant="secondary"
              icon={RefreshCw}
              onClick={fetchCemeteries}
            >
              새로고침
            </Button>
          </div>
        </div>
      </div>

      {/* 묘지 목록 테이블 */}
      {filteredCemeteries.length > 0 ? (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>묘지명</th>
                  <th>주소</th>
                  <th>설명</th>
                  <th>상태</th>
                  <th>생성일</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {filteredCemeteries.map(cemetery => (
                  <tr key={cemetery.id}>
                    <td className="font-medium">
                      {cemetery.name}
                    </td>
                    <td>
                      {cemetery.address || '-'}
                    </td>
                    <td>
                      <div className="text-sm text-gray-600 max-w-xs truncate">
                        {cemetery.description || '-'}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${cemetery.is_active ? 'status-completed' : 'status-cancelled'}`}>
                        {cemetery.is_active ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td className="text-sm text-gray-600">
                      {cemetery.created_at ? new Date(cemetery.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleEdit(cemetery)}
                          className="text-blue-600 hover:text-blue-900"
                          title="수정"
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => handleMapSettings(cemetery)}
                          className="text-green-600 hover:text-green-900"
                          title="지도 설정"
                        >
                          <MapPin size={16} />
                        </button>
                        <button 
                          onClick={() => handleToggleStatus(cemetery)}
                          className={`${cemetery.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                          title={cemetery.is_active ? '비활성화' : '활성화'}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <BarChart2 size={48} className="text-gray-300 mb-4" />
          <p className="text-gray-500">묘지가 없습니다.</p>
        </div>
      )}

      {/* 묘지 추가/수정 모달 */}
      {showForm && (
        <>
          <div className="modal-overlay" onClick={() => setShowForm(false)} />
          <div className="modal-content">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editing ? '묘지 수정' : '묘지 추가'}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  묘지명 *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input w-full"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  주소
                </label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  설명
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input w-full"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                >
                  취소
                </Button>
                <Button type="submit" variant="primary">
                  {editing ? '수정' : '추가'}
                </Button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* 지도 설정 모달 */}
      {showMapSettings && (
        <>
          <div className="modal-overlay" onClick={() => setShowMapSettings(false)} />
          <div className="modal-content" style={{ 
            maxWidth: '800px', 
            maxHeight: '90vh', 
            overflowY: 'auto',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1001
          }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                지도 설정 - {editing?.name}
              </h2>
              <button
                onClick={() => setShowMapSettings(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleMapSettingsSubmit} className="space-y-6">
              {/* 프로필 이미지 섹션 */}
              <div className="border rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Image size={20} className="mr-2" />
                  프로필 이미지
                </h3>
                <div className="flex items-center space-x-4">
                  {profileImage && (
                    <div className="w-24 h-24 border rounded-lg overflow-hidden">
                      <img src={profileImage} alt="프로필" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfileImageChange}
                      className="input w-full"
                    />
                    {profileImage && (
                      <button
                        type="button"
                        onClick={handleDeleteProfileImage}
                        className="mt-2 text-red-600 hover:text-red-900 text-sm"
                      >
                        이미지 삭제
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* 지도 꼭짓점 좌표 섹션 */}
              <div className="border rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <MapPin size={20} className="mr-2" />
                  지도 꼭짓점 좌표
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">북동쪽 위도</label>
                    <input
                      type="number"
                      step="any"
                      value={mapSettings.corners.northEastLat}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        corners: { ...mapSettings.corners, northEastLat: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">북동쪽 경도</label>
                    <input
                      type="number"
                      step="any"
                      value={mapSettings.corners.northEastLng}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        corners: { ...mapSettings.corners, northEastLng: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">북서쪽 위도</label>
                    <input
                      type="number"
                      step="any"
                      value={mapSettings.corners.northWestLat}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        corners: { ...mapSettings.corners, northWestLat: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">북서쪽 경도</label>
                    <input
                      type="number"
                      step="any"
                      value={mapSettings.corners.northWestLng}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        corners: { ...mapSettings.corners, northWestLng: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">남동쪽 위도</label>
                    <input
                      type="number"
                      step="any"
                      value={mapSettings.corners.southEastLat}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        corners: { ...mapSettings.corners, southEastLat: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">남동쪽 경도</label>
                    <input
                      type="number"
                      step="any"
                      value={mapSettings.corners.southEastLng}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        corners: { ...mapSettings.corners, southEastLng: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">남서쪽 위도</label>
                    <input
                      type="number"
                      step="any"
                      value={mapSettings.corners.southWestLat}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        corners: { ...mapSettings.corners, southWestLat: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">남서쪽 경도</label>
                    <input
                      type="number"
                      step="any"
                      value={mapSettings.corners.southWestLng}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        corners: { ...mapSettings.corners, southWestLng: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                </div>
              </div>

              {/* 지도 레벨별 타일 갯수 섹션 */}
              <div className="border rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Settings size={20} className="mr-2" />
                  지도 레벨별 타일 갯수
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">레벨 0 X축</label>
                    <input
                      type="number"
                      value={mapSettings.tiles.level0X}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        tiles: { ...mapSettings.tiles, level0X: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">레벨 0 Y축</label>
                    <input
                      type="number"
                      value={mapSettings.tiles.level0Y}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        tiles: { ...mapSettings.tiles, level0Y: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div></div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">레벨 1 X축</label>
                    <input
                      type="number"
                      value={mapSettings.tiles.level1X}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        tiles: { ...mapSettings.tiles, level1X: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">레벨 1 Y축</label>
                    <input
                      type="number"
                      value={mapSettings.tiles.level1Y}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        tiles: { ...mapSettings.tiles, level1Y: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div></div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">레벨 2 X축</label>
                    <input
                      type="number"
                      value={mapSettings.tiles.level2X}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        tiles: { ...mapSettings.tiles, level2X: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">레벨 2 Y축</label>
                    <input
                      type="number"
                      value={mapSettings.tiles.level2Y}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        tiles: { ...mapSettings.tiles, level2Y: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                </div>
              </div>

              {/* 기본 출발 위치 섹션 */}
              <div className="border rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <MapPin size={20} className="mr-2" />
                  기본 출발 위치
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">기본 출발 위도</label>
                    <input
                      type="number"
                      step="any"
                      value={mapSettings.defaultStartingLocation.defaultStartingLat}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        defaultStartingLocation: { 
                          ...mapSettings.defaultStartingLocation, 
                          defaultStartingLat: e.target.value 
                        }
                      })}
                      className="input w-full"
                      placeholder="예: 37.196944"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1">기본 출발 경도</label>
                    <input
                      type="number"
                      step="any"
                      value={mapSettings.defaultStartingLocation.defaultStartingLng}
                      onChange={(e) => setMapSettings({
                        ...mapSettings,
                        defaultStartingLocation: { 
                          ...mapSettings.defaultStartingLocation, 
                          defaultStartingLng: e.target.value 
                        }
                      })}
                      className="input w-full"
                      placeholder="예: 127.612778"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  GPS가 비활성화되어 있거나 현재 위치가 지도 범위 밖일 때 사용되는 기본 위치입니다.
                </p>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowMapSettings(false)}
                >
                  취소
                </Button>
                <Button type="submit" variant="primary">
                  저장
                </Button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default Cemeteries; 